export type PosSalesLayoutCardAspectRatio = {
  width: number;
  height: number;
};

export type PosSalesLayoutIncludeMode =
  | "AUTO_INCLUDE_PUBLISHED_ELIGIBLE"
  | "CURATED_ONLY";

export type PosSalesLayoutCard = {
  product_id: string;
};

export type PosSalesLayoutSection = {
  section_id: string;
  name?: string | null;
  source_type: "CATEGORY" | "CURATED";
  category_id?: string | null;
  include_mode?: PosSalesLayoutIncludeMode;
  show_header?: boolean;
  cards?: PosSalesLayoutCard[];
};

export type PosSalesLayoutTab = {
  tab_id: string;
  name: string;
  presentation_mode?: "SECTIONED" | "MIXED";
  sections?: PosSalesLayoutSection[];
  manual_product_order?: string[];
};

export type PosSalesLayoutDocument = Record<string, unknown> & {
  header_message?: { enabled: boolean; text: string } | null;
  schema_version?: number;
  tabs?: PosSalesLayoutTab[];
  card_presentation?: {
    aspect_ratio?: PosSalesLayoutCardAspectRatio | null;
  } | null;
};

export type PosSalesLayoutRuntimeEvent = {
  runtime_action: "UPSERT" | "DELETE";
  revision: number;
  organization_id: string;
  branch_id: string;
  layout_id: string | null;
  layout_document: PosSalesLayoutDocument | null;
};

export type PosSalesLayoutRuntimePayload = {
  ok: boolean;
  contract: "GET_POS_SALES_LAYOUT_PUBLISHED_DELTA_V1";
  projection_contract?: "MBOS_GET_POS_SALES_LAYOUT_PUBLISHED_DELTA_V1";
  mode: "FULL" | "DELTA";
  organization_id: string;
  branch_id: string;
  node_id?: string;
  since_revision: number;
  latest_revision: number;
  returned_event_count: number;
  has_more: boolean;
  limit: number;
  events: PosSalesLayoutRuntimeEvent[];
};

type GatewayEnvelope<T> = { ok: boolean; data?: T; error?: string };

export class PosSalesLayoutGatewayClient {
  private readonly baseUrl: string;
  private readonly timeoutMs: number;

  constructor(options: { baseUrl: string; timeoutMs?: number }) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, "");
    this.timeoutMs = options.timeoutMs ?? 15_000;
  }

  async load(input: {
    deviceSecret: string;
    sessionToken: string;
    sinceRevision: number;
    limit?: number;
  }): Promise<{ ok: boolean; data: PosSalesLayoutRuntimePayload | null; error: string | null }> {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), this.timeoutMs);
    try {
      const response = await fetch(`${this.baseUrl}/api/pos/runtime/sales-layout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          device_secret: input.deviceSecret,
          session_token: input.sessionToken,
          since_revision: input.sinceRevision,
          limit: input.limit ?? 50,
        }),
        credentials: "omit",
        signal: controller.signal,
      });
      const payload = (await response.json()) as GatewayEnvelope<PosSalesLayoutRuntimePayload>;
      if (!response.ok || !payload.ok || !payload.data) {
        return { ok: false, data: null, error: payload.error ?? `GATEWAY_HTTP_${response.status}` };
      }
      return { ok: true, data: payload.data, error: null };
    } catch (error) {
      return {
        ok: false,
        data: null,
        error: error instanceof DOMException && error.name === "AbortError" ? "GATEWAY_TIMEOUT" : "GATEWAY_UNREACHABLE",
      };
    } finally {
      window.clearTimeout(timeout);
    }
  }
}
